// 流程回复中的占位符：本站可配置的值（官网、下载地址、查询次数）可在流程页直接查看与修改；运行时数据只做说明
import { reactive } from 'vue'

export const WEBSITE_KEY = 'config/site-global.yaml'
export const RETRY_KEY = 'policies/order-query-retry.yaml'

const known = {
  'siteUrl.url': { label: '官网地址', kind: 'website' },
  'appDownload.downloadLines': { label: '软件下载地址', kind: 'downloads' },
  'amicoDownload.downloadLines': { label: 'Amico 下载地址', kind: 'downloads', product: 'AMICO' },
  'runtime.system.orderNotFound.maxCorrectionRetries': { label: '单号重新查询次数上限', kind: 'retries' },
  'accountBalance.balance': { label: '玩家账户余额', kind: 'runtime' },
  'accountContext.currentBirthday': { label: '玩家当前登记的生日', kind: 'runtime' },
  'accountTotals.days': { label: '统计天数', kind: 'runtime' },
  'accountTotals.depositTotal': { label: '存款总额', kind: 'runtime' },
  'accountTotals.withdrawalTotal': { label: '提款总额', kind: 'runtime' },
  'boundAccounts.summaryText': { label: '已绑定银行卡 / 钱包（末四位）', kind: 'runtime' },
}

export function placeholderInfo(name, slots = {}) {
  if (known[name]) return known[name]
  if (name.startsWith('slots.')) return { label: `玩家提供的${slots[name.slice(6)]?.title || name.slice(6)}`, kind: 'runtime' }
  if (name.startsWith('policy.')) return { label: `业务参数 ${name.slice(7)}`, kind: 'parameter' }
  return { label: name, kind: 'runtime' }
}

export function splitPlaceholders(text) {
  const parts = []; const pattern = /\{\{\s*([^{}]+?)\s*\}\}/g; let last = 0; let match
  const value = String(text ?? '')
  while ((match = pattern.exec(value))) {
    if (match.index > last) parts.push({ text: value.slice(last, match.index) })
    parts.push({ name: match[1].trim() }); last = match.index + match[0].length
  }
  if (last < value.length) parts.push({ text: value.slice(last) })
  return parts
}

export function validUrl(value) {
  try { const url = new URL(String(value).trim()); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password } catch { return false }
}

// request(method, suffix, data, params)：本站站点内容 API（由 AiWorkflowStudio 传入）
export function createSiteValues(site, request) {
  // sharedEnabled：已启用全站共用设定；websiteSource 与各软件的 source 为 SHARED 共用值 / CUSTOM 本站自订 / SITE 本站专属
  const state = reactive({ loaded: false, error: '', websiteUrl: '', websiteSource: '', sharedEnabled: false, downloads: [], maxRetries: null })
  let loading = null
  const revision = async () => (await request('GET', ''))?.revision
  async function load() {
    const [global, directory, retry] = await Promise.all([
      request('GET', '/resource', undefined, { key: WEBSITE_KEY }),
      request('GET', '/directories/downloads'),
      request('GET', '/resource', undefined, { key: RETRY_KEY }),
    ])
    state.websiteUrl = global?.document?.sites?.[site.siteCode]?.links?.website?.url || ''
    state.sharedEnabled = !!global?.sharedEnabled; state.websiteSource = global?.sources?.website || ''
    state.downloads = directory?.entries || []
    state.maxRetries = retry?.document?.orderNotFound?.maxCorrectionRetries ?? null
    state.loaded = true; state.error = ''
  }
  function ensure() {
    if (!loading) loading = load().catch(e => { state.error = e.response?.data?.message || e.message; loading = null })
    return loading
  }
  async function reload() { loading = null; await ensure() }
  // 流程页只能改本站自订（或取消自订）；全站共用值只在「全站共用设定」分页修改
  // 本站官网地址（启用共用设定后即为本站自订）；url 为 null 时取消自订、改用共用值
  async function saveWebsite(url) {
    if (url !== null && !validUrl(url)) throw new Error('请输入完整的 http:// 或 https:// 官网地址')
    await request('PUT', '/website', { expectedRevision: await revision(), url: url === null ? null : url.trim() })
    await reload()
  }
  async function cancelWebsiteCustom() { await saveWebsite(null) }
  const checkUrls = changes => { for (const value of Object.values(changes)) for (const url of [value.androidUrl, value.iosUrl]) if (url && !validUrl(url)) throw new Error('下载地址需为完整的 http:// 或 https:// 地址') }
  // 本站软件下载地址；changes: { 软件代码: { androidUrl, iosUrl } }，共用的软件保存后成为本站自订，其余目录项原样保存
  async function saveDownloads(changes) {
    checkUrls(changes)
    const directory = await request('GET', '/directories/downloads')
    const entries = directory.entries.map(e => changes[e.code] ? { ...e, androidUrl: changes[e.code].androidUrl.trim(), iosUrl: changes[e.code].iosUrl.trim(), ...(e.source === 'SHARED' ? { source: 'CUSTOM' } : {}) } : e)
    await request('PUT', '/directories/downloads', { expectedRevision: directory.revision, entries })
    await reload()
  }
  // 取消本站自订：这些软件改用全站共用值
  async function cancelDownloadCustom(codes) {
    const directory = await request('GET', '/directories/downloads')
    const entries = directory.entries.map(e => codes.includes(e.code) && e.source === 'CUSTOM' ? { ...e, source: 'SHARED' } : e)
    await request('PUT', '/directories/downloads', { expectedRevision: directory.revision, entries })
    await reload()
  }
  async function saveRetries(value) {
    if (!Number.isInteger(value) || value < 1 || value > 10) throw new Error('重新查询次数需为 1～10 的整数')
    const resource = await request('GET', '/resource', undefined, { key: RETRY_KEY })
    const document = JSON.parse(JSON.stringify(resource.document))
    document.orderNotFound = { ...document.orderNotFound, maxCorrectionRetries: value }
    await request('PUT', '/resource', { expectedRevision: await revision(), key: RETRY_KEY, content: JSON.stringify(document) })
    await reload()
  }
  async function saveReply(key, content) {
    await request('PUT', '/reply', { expectedRevision: await revision(), key, content })
  }
  return { state, ensure, reload, saveWebsite, cancelWebsiteCustom, saveDownloads, cancelDownloadCustom, saveRetries, saveReply }
}
